import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  initialState,
  SuperAdminJobPendingApprovalReducer,
} from "./super_admin_jobs_reduser";
import {
  clearHistoryFilters as clearHistoryFiltersAction,
  setActiveTab,
  setApprovalHistory,
  setApprovalStats,
  setCurrentPage as setCurrentPageAction,
  setHistoryError,
  setHistoryLoading,
  setHistorySearchTerm,
  setHistoryStatusFilter,
  setHistoryTotalCount,
  setOpenMenu,
  setPendingCurrentPage as setPendingCurrentPageAction,
  setPendingApprovals,
  setPendingError,
  setPendingLoading,
  setPendingRowsPerPage as setPendingRowsPerPageAction,
  setPendingTotalCount,
  setRowsPerPage as setRowsPerPageAction,
} from "./super_admin_jobs_actions";
import {
  getPendingApprovalDataJobsList,
  getSuperAdminJobApprovals,
  getPlatformApprovalStats,
} from "../api/super_admin_jobs_api";
import { getPlatformApprovalHistory } from "../api/super_admin_jobs_api";
import {
  ApprovalHistoryLoadResult,
  ApprovalHistoryQuery,
  ApprovalStatsSummary,
  ApprovalSummaryCard,
  ApprovalTab,
  ApprovalTableRow,
  HistoryStatusFilter,
  JobsTabKey,
  PendingApprovalsLoadResult,
  PendingApprovalsQuery,
  SuperAdminPendingApprovalJobs,
  normalizeApprovalStats,
  toApprovalHistoryTableRow,
} from "../model/super_admin_jobs_model";

export const useSuperAdminJobsPendingApprovalViewModel = () => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(
    SuperAdminJobPendingApprovalReducer,
    initialState
  );

  const [rejectModal, setRejectModal] = useState<{
    jobId: string;
    rejectReason: string;
  } | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [appliedHistorySearchTerm, setAppliedHistorySearchTerm] =
    useState("");

  const showToastMessage = useCallback(
    (message: string, type: "success" | "error") => {
      setToast({ message, type });
    },
    []
  );

  const closeToast = useCallback(() => setToast(null), []);

  const openRejectModal = useCallback(
    (jobId: string) => setRejectModal({ jobId, rejectReason: "" }),
    []
  );
  const closeRejectModal = useCallback(() => setRejectModal(null), []);

  const updateRejectReason = useCallback((rejectReason: string) => {
    setRejectModal((current) =>
      current ? { ...current, rejectReason } : current
    );
  }, []);

  const fetchPendingApprovals = useCallback(
    async (
      params: PendingApprovalsQuery = {}
    ): Promise<PendingApprovalsLoadResult> => {
      try {
        const resp = await getPendingApprovalDataJobsList(params);
        const data = resp.data as {
          pending_approvals?: unknown;
          total?: unknown;
          has_more?: unknown;
          hasMore?: unknown;
        };
        const approvals = getPendingApprovals(data);
        const total = Number(data.total ?? approvals.length);

        return {
          approvals,
          total: Number.isFinite(total) ? total : approvals.length,
          hasMore: Boolean(data.has_more ?? data.hasMore),
        };
      } catch (error) {
        return {
          approvals: [],
          total: 0,
          hasMore: false,
          error: getApiErrorMessage(error, "Failed to load pending approvals"),
        };
      }
    },
    []
  );

  const loadPendingApprovals = useCallback(
    async (
      params: PendingApprovalsQuery = {}
    ): Promise<PendingApprovalsLoadResult> => {
      dispatch(setPendingLoading(true));
      dispatch(setPendingError(null));

      const result = await fetchPendingApprovals(params);

      dispatch(setPendingApprovals(result.approvals));
      dispatch(setPendingTotalCount(result.total));
      dispatch(setPendingError(result.error ?? null));
      dispatch(setPendingLoading(false));

      if (result.error) {
        showToastMessage(result.error, "error");
      }

      return result;
    },
    [fetchPendingApprovals, showToastMessage]
  );

  const loadPendingApprovalsPage = useCallback(
    () =>
      loadPendingApprovals({
        limit: state.pendingRowsPerPage,
        offset: (state.pendingCurrentPage - 1) * state.pendingRowsPerPage,
      }),
    [
      loadPendingApprovals,
      state.pendingCurrentPage,
      state.pendingRowsPerPage,
    ]
  );

  const clampPendingPage = useCallback(
    (total: number) => {
      const totalPages = Math.max(
        1,
        Math.ceil(total / state.pendingRowsPerPage)
      );

      if (state.pendingCurrentPage > totalPages) {
        dispatch(setPendingCurrentPageAction(totalPages));
      }
    },
    [state.pendingCurrentPage, state.pendingRowsPerPage]
  );

  const fetchApprovalHistory = useCallback(
    async (
      params: ApprovalHistoryQuery = {}
    ): Promise<ApprovalHistoryLoadResult> => {
      try {
        const resp = await getPlatformApprovalHistory(params);
        const data = resp.data as {
          history?: unknown;
          total?: unknown;
          has_more?: unknown;
          hasMore?: unknown;
        };
        const history = Array.isArray(data.history)
          ? (data.history as ApprovalTableRow[])
          : [];
        const total = Number(data.total ?? history.length);

        return {
          history,
          total: Number.isFinite(total) ? total : history.length,
          hasMore: Boolean(data.has_more ?? data.hasMore),
        };
      } catch (error) {
        return {
          history: [],
          total: 0,
          hasMore: false,
          error: getApiErrorMessage(error, "Failed to load approval history"),
        };
      }
    },
    []
  );

  const loadApprovalHistory = useCallback(
    async (
      params: ApprovalHistoryQuery = {}
    ): Promise<ApprovalHistoryLoadResult> => {
      const result = await fetchApprovalHistory(params);
      dispatch(setApprovalHistory(result.history));
      dispatch(setHistoryTotalCount(result.total));
      dispatch(setHistoryError(result.error ?? null));
      return result;
    },
    [fetchApprovalHistory]
  );

  const loadApprovalStats = useCallback(async () => {
    try {
      const resp = await getPlatformApprovalStats();
      return resp.data as Partial<ApprovalStatsSummary>;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, []);

  const refreshApprovalStats = useCallback(async () => {
    const stats = await loadApprovalStats();
    dispatch(setApprovalStats(normalizeApprovalStats(stats)));
  }, [loadApprovalStats]);

  const handleSuperAdminJobApproval = useCallback(
    async (
      requestId: string,
      action: "approve" | "reject",
      rejectionReason: string
    ) => {
      try {
        const payload = {
          action,
          rejection_reason: rejectionReason,
        };
        dispatch(setOpenMenu(null));
        await getSuperAdminJobApprovals(requestId, payload);
        showToastMessage(
          action === "approve"
            ? "Job approved successfully"
            : "Job rejected successfully",
          "success"
        );
        const result = await loadPendingApprovalsPage();
        clampPendingPage(result.total);
        await refreshApprovalStats();
      } catch (error) {
        console.error(error);
        showToastMessage(
          getApiErrorMessage(error, "Something went wrong. Please try again."),
          "error"
        );
      }
    },
    [
      clampPendingPage,
      loadPendingApprovalsPage,
      refreshApprovalStats,
      showToastMessage,
    ]
  );

  useEffect(() => {
    if (state.activeTab !== "pending") return;

    let isActive = true;

    const fetchPending = async () => {
      dispatch(setPendingLoading(true));
      dispatch(setPendingError(null));

      const result = await fetchPendingApprovals({
        limit: state.pendingRowsPerPage,
        offset: (state.pendingCurrentPage - 1) * state.pendingRowsPerPage,
      });

      if (!isActive) return;

      dispatch(setPendingApprovals(result.approvals));
      dispatch(setPendingTotalCount(result.total));
      dispatch(setPendingError(result.error ?? null));
      dispatch(setPendingLoading(false));

      if (result.error) {
        showToastMessage(result.error, "error");
      }
    };

    void fetchPending();

    return () => {
      isActive = false;
    };
  }, [
    fetchPendingApprovals,
    showToastMessage,
    state.activeTab,
    state.pendingCurrentPage,
    state.pendingRowsPerPage,
  ]);

  useEffect(() => {
    void refreshApprovalStats();
  }, [refreshApprovalStats]);

  useEffect(() => {
    if (state.activeTab !== "history") return;

    let isActive = true;

    const fetchHistory = async () => {
      dispatch(setHistoryLoading(true));
      dispatch(setHistoryError(null));

      const result = await fetchApprovalHistory({
        status:
          state.historyStatusFilter === "all"
            ? undefined
            : state.historyStatusFilter,
        search: appliedHistorySearchTerm.trim() || undefined,
        limit: state.rowsPerPage,
        offset: (state.currentPage - 1) * state.rowsPerPage,
      });

      if (!isActive) return;

      dispatch(setApprovalHistory(result.history));
      dispatch(setHistoryTotalCount(result.total));
      dispatch(setHistoryError(result.error ?? null));
      dispatch(setHistoryLoading(false));
    };

    void fetchHistory();

    return () => {
      isActive = false;
    };
  }, [
    appliedHistorySearchTerm,
    fetchApprovalHistory,
    state.activeTab,
    state.currentPage,
    state.historyStatusFilter,
    state.rowsPerPage,
  ]);

  const historyRows = useMemo(
    () => state.approvalHistory.map(toApprovalHistoryTableRow),
    [state.approvalHistory]
  );

  const pendingTotalPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(state.pendingTotalCount / state.pendingRowsPerPage)
      ),
    [state.pendingRowsPerPage, state.pendingTotalCount]
  );

  useEffect(() => {
    if (state.pendingCurrentPage > pendingTotalPages) {
      dispatch(setPendingCurrentPageAction(pendingTotalPages));
    }
  }, [pendingTotalPages, state.pendingCurrentPage]);

  const historyTotalPages = useMemo(
    () => Math.max(1, Math.ceil(state.historyTotalCount / state.rowsPerPage)),
    [state.historyTotalCount, state.rowsPerPage]
  );

  useEffect(() => {
    if (state.currentPage > historyTotalPages) {
      dispatch(setCurrentPageAction(historyTotalPages));
    }
  }, [historyTotalPages, state.currentPage]);

  const summaryCards = useMemo<ApprovalSummaryCard[]>(
    () => [
      {
        label: "Pending",
        value: state.approvalStats.pending,
        accent: "border-l-blue-500",
        filter: "pending",
      },
      {
        label: "Approved",
        value: state.approvalStats.approved,
        accent: "border-l-green-500",
        filter: "approved",
      },
      {
        label: "Rejected",
        value: state.approvalStats.rejected,
        accent: "border-l-red-500",
        filter: "rejected",
      },
      {
        label: "Total",
        value: state.approvalStats.total,
        accent: "border-l-sky-500",
        filter: "all",
      },
    ],
    [state.approvalStats]
  );

  const tabs = useMemo<ApprovalTab[]>(
    () => [
      {
        key: "pending",
        label: "Pending Approvals",
        count:
          state.approvalStats.pending ||
          state.pendingTotalCount ||
          state.pendingApprovals.length,
      },
      {
        key: "history",
        label: "Approval History",
        count: state.approvalStats.total || state.historyTotalCount,
      },
    ],
    [
      state.approvalStats.pending,
      state.approvalStats.total,
      state.historyTotalCount,
      state.pendingApprovals.length,
      state.pendingTotalCount,
    ]
  );

  const hasHistoryFilters =
    state.historySearchTerm.trim() !== "" ||
    appliedHistorySearchTerm.trim() !== "" ||
    state.historyStatusFilter !== "all";

  const handleTabChange = useCallback((tab: JobsTabKey) => {
    dispatch(setActiveTab(tab));
  }, []);

  const handleHistoryStatusFilter = useCallback(
    (filter: HistoryStatusFilter) => {
      dispatch(setHistoryStatusFilter(filter));
    },
    []
  );

  const updateHistorySearchTerm = useCallback((value: string) => {
    dispatch(setHistorySearchTerm(value));
  }, []);

  const applyHistorySearch = useCallback(() => {
    setAppliedHistorySearchTerm(state.historySearchTerm.trim());
    dispatch(setCurrentPageAction(1));
  }, [state.historySearchTerm]);

  const clearHistoryFilters = useCallback(() => {
    setAppliedHistorySearchTerm("");
    dispatch(clearHistoryFiltersAction());
  }, []);

  const handlePageChange = useCallback((page: number) => {
    dispatch(setCurrentPageAction(Math.max(1, page)));
  }, []);

  const handleRowsPerPageChange = useCallback((rows: number) => {
    dispatch(setRowsPerPageAction(Math.max(1, rows)));
  }, []);

  const handlePendingPageChange = useCallback((page: number) => {
    dispatch(setPendingCurrentPageAction(Math.max(1, page)));
  }, []);

  const handlePendingRowsPerPageChange = useCallback((rows: number) => {
    dispatch(setPendingRowsPerPageAction(Math.max(1, rows)));
  }, []);

  const toggleJobMenu = useCallback(
    (jobId: string) => {
      dispatch(setOpenMenu(state.openMenuJobId === jobId ? null : jobId));
    },
    [state.openMenuJobId]
  );

  const handleAction = useCallback(
    (jobId: string, action: "approve" | "reject") => {
      if (action === "reject") {
        openRejectModal(jobId);
        return;
      }

      void handleSuperAdminJobApproval(jobId, action, "");
    },
    [handleSuperAdminJobApproval, openRejectModal]
  );

  const submitRejectModal = useCallback(() => {
    if (!rejectModal) return;

    void handleSuperAdminJobApproval(
      rejectModal.jobId,
      "reject",
      rejectModal.rejectReason
    );
    closeRejectModal();
  }, [closeRejectModal, handleSuperAdminJobApproval, rejectModal]);

  const handleViewApproval = (row: ApprovalTableRow) => {
    navigate(`/super-admin/PendingJobDetails/${row.request_id}`);
  };

  return {
    state,
    tabs,
    summaryCards,
    historyRows,
    pendingTotalPages,
    historyTotalPages,
    hasHistoryFilters,
    loadPendingApprovals,
    loadApprovalHistory,
    loadApprovalStats,
    handleTabChange,
    handleHistoryStatusFilter,
    updateHistorySearchTerm,
    applyHistorySearch,
    clearHistoryFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handlePendingPageChange,
    handlePendingRowsPerPageChange,
    toggleJobMenu,
    handleAction,
    handleViewApproval,
    handleSuperAdminJobApproval,
    rejectModal,
    updateRejectReason,
    closeRejectModal,
    submitRejectModal,
    toast,
    closeToast,
  };
};

function getPendingApprovals(data: unknown): SuperAdminPendingApprovalJobs[] {
  if (Array.isArray(data)) {
    return data as SuperAdminPendingApprovalJobs[];
  }

  const responseData = data as { pending_approvals?: unknown };
  return Array.isArray(responseData.pending_approvals)
    ? (responseData.pending_approvals as SuperAdminPendingApprovalJobs[])
    : [];
}

function getApiErrorMessage(error: unknown, fallback: string) {
  const apiError = error as {
    response?: {
      data?: {
        message?: unknown;
        detail?: unknown;
      };
    };
    message?: unknown;
  };
  const message =
    apiError.response?.data?.message ??
    apiError.response?.data?.detail ??
    apiError.message;

  return typeof message === "string" && message.trim() ? message : fallback;
}
